import React, { useEffect, useState } from "react";
import { useLazyGetCmsQuery } from "../../services/cms";
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Container, 
  Grid, 
  IconButton,
  Chip,
  Fade,
  Slide
} from "@mui/material";
import { 
  Email as EmailIcon, 
  Phone as PhoneIcon, 
  Support as SupportIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon
} from "@mui/icons-material";

const ContactSupport = () => {
  const [getCms] = useLazyGetCmsQuery();
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [copiedField, setCopiedField] = useState<string>("");

  const getCmsData = async () => {
    try {
      setIsLoading(true);
      const response = await getCms({}).unwrap();
      if (response.statusCode === 200) {
        setEmail(response?.data?.email || "");
        setPhone(response?.data?.phone || "");
        setCountryCode(response?.data?.countryCode || "");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCmsData();
  }, []);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(""), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const contactItems = [
    {
      icon: <EmailIcon sx={{ fontSize: 40, color: '#C14A09' }} />,
      title: "Email Support",
      value: email || "Support@awnservices.com",
      subtitle: "Send us an email anytime",
      action: () => handleCopy(email || "Support@awnservices.com", "email"),
      href: email ? `mailto:${email}` : "mailto:Support@awnservices.com",
      available: !!email
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 40, color: '#C14A09' }} />,
      title: "Phone Support",
      value: phone ? `${countryCode}${phone}` : "+971502940051",
      subtitle: "Call us during business hours",
      action: () => handleCopy(phone ? `${countryCode}${phone}` : "+971502940051", "phone"),
      href: phone ? `tel:${countryCode}${phone}` : "tel:+971502940051",
      available: !!phone
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40, color: '#C14A09' }} />,
      title: "Website",
      value: "http://awnservices.com",
      subtitle: "Visit our official website",
      action: () => handleCopy("http://awnservices.com", "website"),
      href: "http://awnservices.com",
      available: true
    }
  ];

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #C14A09 0%, #8B2E00 100%)',
        py: { xs: 4, md: 8 },
        px: { xs: 2, sm: 3 }
      }}
    >
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box sx={{ mb: 3 }}>
              <SupportIcon 
                sx={{ 
                  fontSize: { xs: 60, md: 80 }, 
                  color: 'white',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                }} 
              />
            </Box>
            <Typography 
              variant="h2" 
              component="h1"
              sx={{ 
                color: 'white',
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Contact Us
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                maxWidth: '600px',
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              AWN Services App - Get in touch with our support team for any questions or assistance.
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={4} justifyContent="center">
          {contactItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Slide 
                direction="up" 
                in 
                timeout={1000 + index * 200}
              >
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    backdropFilter: 'blur(10px)',
                    background: 'rgba(255,255,255,0.95)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      {item.icon}
                    </Box>
                    
                    <Typography 
                      variant="h5" 
                      component="h3"
                      sx={{ 
                        fontWeight: 'bold',
                        mb: 1,
                        color: '#333'
                      }}
                    >
                      {item.title}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        mb: 3,
                        fontSize: '0.95rem'
                      }}
                    >
                      {item.subtitle}
                    </Typography>

                    <Box 
                      sx={{ 
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        mb: 3,
                        position: 'relative'
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: '600',
                          color: '#495057',
                          wordBreak: 'break-all',
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        }}
                      >
                        {item.value}
                      </Typography>
                      
                      <IconButton
                        onClick={item.action}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: copiedField === item.title.toLowerCase().split(' ')[0] ? '#008000' : '#F5F8FA',
                          color: copiedField === item.title.toLowerCase().split(' ')[0] ? 'white' : '#C14A09',
                          '&:hover': {
                            backgroundColor: copiedField === item.title.toLowerCase().split(' ')[0] ? '#006600' : '#E7E7E7',
                          },
                          transition: 'all 0.3s ease'
                        }}
                        size="small"
                      >
                        {copiedField === item.title.toLowerCase().split(' ')[0] ? <CheckIcon /> : <CopyIcon />}
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Chip
                        label={item.available ? "Available" : "Not Available"}
                        color={item.available ? "success" : "default"}
                        size="small"
                        sx={{ fontWeight: '500' }}
                      />
                      
                      {item.available && (
                        <Chip
                          label="Click to Copy"
                          variant="outlined"
                          size="small"
                          sx={{ 
                            fontWeight: '500',
                            borderColor: '#C14A09',
                            color: '#C14A09'
                          }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
          ))}
        </Grid>

        <Fade in timeout={1500}>
          <Box 
            sx={{ 
              textAlign: 'center', 
              mt: 6,
              p: 4,
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'white',
                mb: 2,
                fontWeight: '600'
              }}
            >
              UAE Compliance
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                fontSize: { xs: '0.9rem', md: '1rem' }
              }}
            >
              For UAE-specific queries, we comply with applicable data protection regulations.
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                mt: 2,
                fontStyle: 'italic'
              }}
            >
              Last Updated: September 15, 2025
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ContactSupport;
